import {
    CostCentreGet,
    CostObjectGet,
    Ulid,
} from "@kase/my-marathon-communication";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import { ApiFilter, CostCentre, CostObject } from "./interfaces";

const getFilterKey = (filter: ApiFilter) => `${filter.company}|${filter.year}`;

const getEntityKey = (id: Readonly<Ulid>, company: string, year: number) =>
    `${id.toString()}|${company}|${year}`;

export const makeCost = ({ connection }: ApiSliceParams) => {
    const centreRequestStore: Record<string, boolean> = {};
    const objectRequestStore: Record<string, boolean> = {};

    const { items: centres, listen: listenCentres } = createListController<
        CostCentreGet,
        CostCentre,
        ApiFilter
    >({
        connection,
        list: (con, filter) => con.cost_centre.items(filter),
        getKey: getFilterKey,
        getId: (item, filter) =>
            getEntityKey(item.id, filter.company, filter.year),
    });
    const { items: objects, listen: listenObjects } = createListController<
        CostObjectGet,
        CostObject,
        ApiFilter
    >({
        connection,
        list: (con, filter) => con.cost_object.items(filter),
        getKey: getFilterKey,
        getId: (item, filter) =>
            getEntityKey(item.id, filter.company, filter.year),
    });

    const loadCentre = async (
        id: Readonly<Ulid>,
        company: string,
        year: number,
    ) => {
        const result: CostCentre[] = [];

        for await (const item of connection.value.cost_centre.get({
            id: [id],
            company,
            year,
        })) {
            const centre = Object.assign(item, {
                id: getEntityKey(item.id, company, year),
                _id: item.id,
            });

            result.push(centre);
        }

        centres.value = upsert(centres.peek().slice(), result, "id");

        return result[0];
    };

    const loadObject = async (
        id: Readonly<Ulid>,
        company: string,
        year: number,
    ) => {
        const result: CostObject[] = [];

        for await (const item of connection.value.cost_object.get({
            id: [id],
            company,
            year,
        })) {
            const object = Object.assign(item, {
                id: getEntityKey(item.id, company, year),
                _id: item.id,
            });

            result.push(object);
        }

        objects.value = upsert(objects.peek().slice(), result, "id");

        return result[0];
    };

    const getCentre = (
        id: Readonly<Ulid> | null | undefined,
        company: string,
        year: number,
    ) => {
        if (!id || !company || !year) {
            return null;
        }

        const key = getEntityKey(id, company, year);

        if (!centreRequestStore[key]) {
            centreRequestStore[key] = true;

            loadCentre(id, company, year);
        }

        return centres.value.find((c) => c.id === key) ?? null;
    };

    const getObject = (
        id: Readonly<Ulid> | null | undefined,
        company: string,
        year: number,
    ) => {
        if (!id || !company || !year) {
            return null;
        }

        const key = getEntityKey(id, company, year);

        if (!objectRequestStore[key]) {
            objectRequestStore[key] = true;

            loadObject(id, company, year);
        }

        return objects.value.find((o) => o.id === key) ?? null;
    };

    void listenCentres;
    void listenObjects;

    return {
        centres,
        objects,
        getCentre,
        getObject,
    };
};
